import { addedToCartItemsAtom } from '@feat/feat-cart/added-to-cart'
import { useCartItem } from '@feat/feat-cart/item'
import { useUrlState } from '@feat/feat-url-state/use-url-state'
import { formatIsoDate } from '@utils/utils-format'

import { useSetAtom } from 'jotai'

import { useRecommendations } from './use-recommendations'

type AccommodationInput = {
  roomId: string
  rateId: string
  accommodationId: number
  checkin: string
  checkout: string
  adultCount: number
  childrenAges: number[]
}

type MutationOptions = {
  onSuccess?: () => void
}

export type AccommodationMutationInput = {
  id: number
  name: string
}

type UseAccommodationMutationInput = {
  accommodation: AccommodationMutationInput
  itemId?: string | null
  rateId?: string | null
  room?: {
    id: string
    images: {
      url: string
      alt: string
    }[]
  } | null
}

export function useAccommodationMutation(accommodationId: number) {
  const { urlState } = useUrlState()
  const { addAccommodationAsync, updateAccommodation: updateAccommodationMutation } = useCartItem()
  const { fetchRecommendations } = useRecommendations(accommodationId)

  const setAddedToCartItemsAtom = useSetAtom(addedToCartItemsAtom)

  const mutate = async (
    mode: 'add' | 'update',
    { accommodation, itemId, rateId, room }: UseAccommodationMutationInput,
    options?: MutationOptions
  ) => {
    if (!rateId || !room || !urlState.from || !urlState.to) return null

    const input: AccommodationInput = {
      roomId: room.id,
      rateId,
      accommodationId: accommodation.id,
      checkin: formatIsoDate(urlState.from),
      checkout: formatIsoDate(urlState.to),
      adultCount: urlState.adults,
      childrenAges: urlState.children?.filter(Boolean).map(Number) ?? [],
    }

    if (mode === 'update' && itemId) {
      updateAccommodationMutation({ itemId, input }, { onSuccess: options?.onSuccess })
      return null
    }

    try {
      await addAccommodationAsync({ input })
    } catch {
      return null
    }
    await fetchRecommendations()

    const [image] = room.images
    setAddedToCartItemsAtom([
      {
        type: 'accommodation',
        name: accommodation.name,
        image,
        startDate: input.checkin,
        endDate: input.checkout,
      },
    ])
    options?.onSuccess?.()
    return true
  }

  const addAccommodation = (input: UseAccommodationMutationInput, options?: MutationOptions) =>
    mutate('add', input, options)

  const updateAccommodation = (input: UseAccommodationMutationInput, options?: MutationOptions) =>
    mutate('update', input, options)

  return { addAccommodation, updateAccommodation }
}
